import { NgIf } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  HostListener,
  computed,
  inject,
  signal,
  viewChild
} from '@angular/core';
import { TranslatePipe } from '@ngx-translate/core';
import {
  LanguageService,
  type SupportedLanguage
} from '../../../core/services/language.service';

/**
 * Selector de idioma ubicado en el header.
 * Reproduce el diseño de Figma: botón con bandera, código de idioma (GAL/ESP)
 * y chevron; al abrirse muestra el idioma actual y la opción alternativa.
 */
@Component({
  selector: 'app-language-selector',
  standalone: true,
  imports: [NgIf, TranslatePipe],
  templateUrl: './language-selector.component.html',
  styleUrl: './language-selector.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class LanguageSelectorComponent {
  private readonly languageService = inject(LanguageService);
  private readonly hostElement = inject(ElementRef);

  readonly dropdown = viewChild<ElementRef<HTMLElement>>('dropdown');

  readonly isOpen = signal(false);
  readonly current = computed(() => this.languageService.currentLanguage());
  readonly alternative = computed(() =>
    this.languageService.availableLanguages.find(
      lang => lang.code !== this.current().code
    )!
  );

  toggle(event: MouseEvent): void {
    event.stopPropagation();
    this.isOpen.update(open => !open);
  }

  select(lang: SupportedLanguage, event: MouseEvent): void {
    event.stopPropagation();
    this.languageService.setLang(lang);
    this.isOpen.set(false);
  }

  close(): void {
    this.isOpen.set(false);
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (!this.isOpen()) {
      return;
    }
    const target = event.target as Node;
    const dropdownEl = this.dropdown()?.nativeElement;
    const hostEl = this.hostElement.nativeElement as Node;
    if (dropdownEl && dropdownEl.contains(target)) {
      return;
    }
    if (hostEl.contains(target)) {
      return;
    }
    this.close();
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    this.close();
  }
}
